import { useState } from 'react';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Carousel } from '@astryxdesign/core/Carousel';
import { Heading } from '@astryxdesign/core/Heading';
import { Icon } from '@astryxdesign/core/Icon';
import { HStack, VStack } from '@astryxdesign/core/Layout';
import { Lightbox } from '@astryxdesign/core/Lightbox';
import { List, ListItem } from '@astryxdesign/core/List';
import { Text } from '@astryxdesign/core/Text';
import { Banner } from '@astryxdesign/core/Banner';
import {
  ArrowUpRight,
  Car,
  Clock,
  Coffee,
  CreditCard,
  Drop,
  MapPin,
  Medal,
  Phone,
  ShieldCheck,
  Sparkle,
  Wrench,
  MapTrifold,
  Scissors,
  Heart,
  Flower,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { useTenant } from '@/app/tenant-context.ts';
import { mediaUrl } from '@/lib/env.ts';
import { money, duration, WEEKDAY_SHORT } from '@/lib/format.ts';
import { Reveal } from '@/components/Reveal.tsx';
import { useOpenBooking } from '@/features/booking/nav.ts';
import { InstallHint } from './InstallHint.tsx';
import { useIndustry } from '@/lib/industry.ts';

const CARD_ICONS: Record<string, PhosphorIcon> = {
  clock: Clock,
  shield: ShieldCheck,
  sparkle: Sparkle,
  car: Car,
  drop: Drop,
  medal: Medal,
  wrench: Wrench,
  coffee: Coffee,
  card: CreditCard,
  map: MapTrifold,
  scissors: Scissors,
  heart: Heart,
  flower: Flower,
};

export function HomePage() {
  const { tenant } = useTenant();
  const copy = useIndustry();
  const openBooking = useOpenBooking();
  const [lightbox, setLightbox] = useState<number | null>(null);
  const hero = tenant.media.find((m) => m.kind === 'hero');
  const logo = tenant.media.find((m) => m.kind === 'logo');
  const works = tenant.media.filter((m) => m.kind === 'work');
  const c = tenant.content;

  return (
    <VStack gap={6}>
      <header className="hero">
        {hero && <img crossOrigin="anonymous" className="hero__img" src={mediaUrl(hero.path, hero.updated_at)} alt="" fetchPriority="high" decoding="async" />}
        <span className="hero__shade" aria-hidden="true" />
        <VStack gap={3} className="hero__content">
          <HStack gap={3} vAlign="center">
            {logo && <img crossOrigin="anonymous" className="hero__logo" src={mediaUrl(logo.path, logo.updated_at)} alt="" width={56} height={56} />}
            <VStack gap={0.5}>
              <Heading level={1} type="display-3">
                {tenant.name}
              </Heading>
              {c.tagline && <Text color="secondary">{c.tagline}</Text>}
            </VStack>
          </HStack>
          {/* Inside the hero, so it scrolls with the photo and only ever blurs the photo. */}
          <Button
            label="Записаться"
            variant={'glass' as 'primary'}
            size="lg"
            width="100%"
            endContent={<Icon icon={ArrowUpRight} />}
            onClick={() => openBooking()}
          />
        </VStack>
      </header>

      {tenant.status === 'preview' && (
        <VStack paddingInline={4}>
          <Banner status="info" title="Демо-версия" description="Записи в этом режиме тестовые, уведомления не отправляются." collapsible={false} />
        </VStack>
      )}

      <Reveal label={`О ${copy.placePrep}`}>
        <VStack gap={4} paddingInline={4}>
          {c.about && (
            <Text color="secondary" textWrap="pretty">
              {c.about}
            </Text>
          )}
          <Card padding={2}>
            <List density="compact">
              {c.infoCards.map((card) => (
                <ListItem
                  key={card.title}
                  label={card.title}
                  description={card.text}
                  startContent={<Icon icon={CARD_ICONS[card.icon] ?? Sparkle} color="accent" size="lg" />}
                />
              ))}
            </List>
          </Card>
        </VStack>
      </Reveal>

      <Reveal label={`Запись в ${copy.placeAcc}`}>
        <VStack gap={3} paddingInline={4}>
          <Heading level={2}>Запись в {copy.placeAcc}</Heading>
          <Card padding={1}>
            <List hasDividers>
              {tenant.services.slice(0, 6).map((s) => (
                <ListItem
                  key={s.id}
                  label={s.name}
                  description={duration(s.duration_minutes)}
                  endContent={<Text weight="semibold">{money(s.price_cents, tenant.currency, s.price_is_from)}</Text>}
                  onClick={() => openBooking({ serviceId: s.id })}
                />
              ))}
            </List>
          </Card>
          <Button
            label="Выбрать услугу и время"
            variant={'glass' as 'primary'}
            size="lg"
            width="100%"
            endContent={<Icon icon={ArrowUpRight} />}
            onClick={() => openBooking()}
          />
        </VStack>
      </Reveal>

      {works.length > 0 && (
        <Reveal label="Наши работы">
          <VStack gap={3}>
            <VStack paddingInline={4}>
              <Heading level={2}>Наши работы</Heading>
            </VStack>
            <Carousel gap={3} padding={4} hasSnap aria-label="Фотографии работ">
              {works.map((w, i) => (
                <button key={w.id} type="button" className="work-tile" onClick={() => setLightbox(i)} aria-label={`Открыть фото: ${w.caption || 'работа'}`}>
                  <img crossOrigin="anonymous" className="work-img" src={mediaUrl(w.path, w.updated_at)} alt={w.caption} loading="lazy" decoding="async" />
                  {w.caption && (
                    <Text type="supporting" display="block" maxLines={1}>
                      {w.caption}
                    </Text>
                  )}
                </button>
              ))}
            </Carousel>
            <Lightbox
              isOpen={lightbox !== null}
              onOpenChange={(open) => !open && setLightbox(null)}
              index={lightbox ?? 0}
              onIndexChange={setLightbox}
              media={works.map((w) => ({ type: 'image' as const, src: mediaUrl(w.path, w.updated_at), alt: w.caption || 'Фото работы' }))}
              hasZoom
            />
          </VStack>
        </Reveal>
      )}

      <Reveal label="Как нас найти">
        <VStack gap={3} paddingInline={4}>
          <Heading level={2}>Как нас найти</Heading>
          <Card padding={1}>
            <List hasDividers>
              <ListItem
                label={c.address}
                description={c.mapUrl ? 'Открыть на карте' : undefined}
                startContent={<Icon icon={MapPin} color="accent" />}
                href={c.mapUrl}
                target="_blank"
              />
              <ListItem label={c.phoneDisplay ?? c.phone} description="Позвонить" startContent={<Icon icon={Phone} color="accent" />} href={`tel:${c.phone}`} />
            </List>
          </Card>
          <Card>
            <Hours />
          </Card>
          {c.bookingNote && <Text type="supporting">{c.bookingNote}</Text>}
        </VStack>
      </Reveal>

      <InstallHint />
    </VStack>
  );
}

function Hours() {
  const { tenant } = useTenant();
  const byDay = new Map<number, string[]>();
  for (const h of tenant.hours) {
    const list = byDay.get(h.weekday) ?? [];
    list.push(`${h.opens.slice(0, 5)}–${h.closes.slice(0, 5)}`);
    byDay.set(h.weekday, list);
  }
  return (
    <VStack gap={1}>
      <Text weight="semibold">Часы работы</Text>
      {[1, 2, 3, 4, 5, 6, 7].map((d) => (
        <HStack key={d} hAlign="between">
          <Text color="secondary">{WEEKDAY_SHORT[d]}</Text>
          <Text hasTabularNumbers>{byDay.get(d)?.join(', ') ?? 'выходной'}</Text>
        </HStack>
      ))}
      {tenant.exceptions.map((e) => (
        <HStack key={e.day} hAlign="between">
          <Text color="secondary">{e.day.split('-').reverse().slice(0, 2).join('.')}</Text>
          <Text>{e.closed ? `закрыто${e.note ? ` — ${e.note}` : ''}` : `${e.opens?.slice(0, 5)}–${e.closes?.slice(0, 5)}`}</Text>
        </HStack>
      ))}
    </VStack>
  );
}
